// ===================================================
// Parent Login Page - Password Authentication
// State machine: phone → password → [success | forgot-otp → reset-password | force-change | device-otp]
// ===================================================

import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, ArrowLeft, Baby, CheckCircle, Sun, Moon, UserPlus, Clock, AlertCircle, Info, Lock, Eye, EyeOff, KeyRound, User, Mail } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL, authApi } from '../../services/api';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import { initPushNotifications } from '../../services/pushNotifications';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { parentLoginHelpTexts } from '../../constants/helpTexts';

const OTP_EXPIRATION_SECONDS = 5 * 60; // 5 minutes
const MAX_RESEND_ATTEMPTS = 3;

type LoginStep = 'phone' | 'password' | 'forgot-otp' | 'reset-password' | 'force-change' | 'device-otp';

// Message Modal Component
interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'info' | 'error' | 'success';
}

function MessageModal({ isOpen, onClose, title, message, type = 'info' }: MessageModalProps) {
  if (!isOpen) return null;

  const colors = {
    info: { bg: '#dbeafe', icon: '#3b82f6', border: '#93c5fd' },
    error: { bg: '#fee2e2', icon: '#ef4444', border: '#fca5a5' },
    success: { bg: '#dcfce7', icon: '#22c55e', border: '#86efac' },
  };

  const IconComponent = type === 'error' ? AlertCircle : type === 'success' ? CheckCircle : Info;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '360px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: colors[type].bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <IconComponent size={20} color={colors[type].icon} />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>{title}</h3>
            </div>
            <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px', lineHeight: 1.5 }}>{message}</p>
            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'white',
                background: colors[type].icon,
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            >
              אישור
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Password validation helper
function validatePassword(password: string): string[] {
  const errors: string[] = [];
  if (password.length < 8) errors.push('לפחות 8 תווים');
  if (!/[A-Z]/.test(password)) errors.push('אות גדולה באנגלית');
  if (!/[a-z]/.test(password)) errors.push('אות קטנה באנגלית');
  if (!/[0-9]/.test(password)) errors.push('ספרה');
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('תו מיוחד');
  return errors;
}

export default function ParentLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresPasswordChange, setRequiresAgreement } = useAuthStore();

  useEffect(() => {
    document.title = 'קיפיקיד — הורים';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#f97316');
  }, []);

  // Get redirect URL from query params (for returning after login)
  const redirectUrl = searchParams.get('redirect');

  const [step, setStep] = useState<LoginStep>('phone');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  // Actual phone number for OTP (may differ from login identifier if user logged in with username/email)
  const [otpPhone, setOtpPhone] = useState('');

  // Temp token for force-change (user authenticated but must change password)
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [tempUser, setTempUser] = useState<any>(null);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'info' | 'error' | 'success';
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  const showMessage = (title: string, message: string, type: 'info' | 'error' | 'success' = 'info') => {
    setModalState({ isOpen: true, title, message, type });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  // Auto-focus OTP input when entering OTP steps
  useEffect(() => {
    if (step === 'forgot-otp' || step === 'device-otp') {
      const focusTimer = setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        if (firstInput) {
          firstInput.focus();
          firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      return () => clearTimeout(focusTimer);
    }
  }, [step]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setInterval(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownSeconds]);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Complete login - set user + token + navigate
  const completeLogin = (data: any) => {
    // Clear previous user's cached data
    localStorage.removeItem('kipikid-children');

    setUser({
      id: data.user.id?.toString() || data.user.publicId,
      email: data.user.email || `${phone}@kipikid.local`,
      name: `${data.user.firstName} ${data.user.lastName}`,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      phone: data.user.phone || phone,
      role: 'parent',
      publicId: data.user.publicId,
    });

    const token = data.accessToken || data.token;
    setToken(token);

    // שמירת refreshToken ו-expiresAt לחידוש אוטומטי בעת תפוגת הטוקן
    const refreshToken = data.refreshToken || data.RefreshToken;
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    const expiresAt = data.expiresAt || data.ExpiresAt;
    if (expiresAt) {
      setTokenExpiresAt(expiresAt);
    }

    setRequiresPasswordChange(false);

    // Check if agreement is required
    if (data.requiresAgreement) {
      setRequiresAgreement(true, data.agreementVersion);
      navigate('/agreement-approval', { replace: true });
      return;
    }

    // רישום ל-Push notifications (אם ההרשאה כבר ניתנה)
    initPushNotifications().catch(() => {});

    const targetUrl = redirectUrl || '/parent/dashboard';
    navigate(targetUrl);
  };

  // Check if input looks like a phone number
  const isPhoneInput = /^\d+$/.test(phone);
  const isValidLogin = isPhoneInput ? phone.length >= 10 : phone.length >= 3;

  // Step 1: Phone/Email/Username submit → go to password
  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPassword('');
    setStep('password');
  };

  // Step 2: Password submit → call parent-login API
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const deviceFingerprint = await getDeviceFingerprint();
      const result = await authApi.parentLogin(phone, password, deviceFingerprint);

      if (result.success && result.data) {
        // Check if password change is required
        if (result.data.requiresPasswordChange) {
          // Store temp token for change-password API call
          setTempToken(result.data.accessToken || result.data.token);
          setTempUser(result.data);
          setNewPassword('');
          setConfirmPassword('');
          setStep('force-change');
          return;
        }

        // Success - complete login
        completeLogin(result.data);
      } else {
        // Check for UNKNOWN_DEVICE error (can be in errorCode or message)
        if (result.errorCode === 'UNKNOWN_DEVICE' || result.message === 'UNKNOWN_DEVICE') {
          // Use actual phone from response (when login was by username/email), fallback to entered value
          const actualPhone = result.data?.phone || result.data?.Phone || phone;
          setOtpPhone(actualPhone);
          await sendOtp(actualPhone);
          setStep('device-otp');
          return;
        }

        showMessage('שגיאה', result.message || 'שם משתמש או סיסמה שגויים', 'error');
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      // Check for UNKNOWN_DEVICE error (can be in errorCode or message)
      if (errorData?.errorCode === 'UNKNOWN_DEVICE' || errorData?.message === 'UNKNOWN_DEVICE') {
        const actualPhone = errorData?.data?.phone || errorData?.data?.Phone || phone;
        setOtpPhone(actualPhone);
        await sendOtp(actualPhone);
        setStep('device-otp');
        return;
      }

      // Show server error message for 401 (wrong password), else generic network error
      if (error?.response?.status === 401 && errorData?.message) {
        showMessage('שגיאה', errorData.message, 'error');
      } else {
        console.error('Login error:', error);
        showMessage('שגיאת רשת', 'בדוק את החיבור לאינטרנט ונסה שוב', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Send OTP helper
  const sendOtp = async (phoneOverride?: string) => {
    const otpTarget = phoneOverride || otpPhone || phone;
    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Phone: otpTarget }),
      });

      const data = await response.json();

      if (data.success) {
        setCooldownSeconds(OTP_EXPIRATION_SECONDS);
        setResendAttempts(0);
        setOtp(['', '', '', '', '', '']);

        // Dev mode: show OTP in console
        if (data.data?.devOtp) {
          console.log('%c DEV MODE - OTP Code: ' + data.data.devOtp, 'background: #22c55e; color: white; padding: 8px; font-size: 16px;');
          showMessage('מצב פיתוח', `קוד OTP: ${data.data.devOtp}`, 'info');
        }
      } else {
        showMessage('שגיאה', data.message || 'שגיאה בשליחת קוד האימות', 'error');
      }
    } catch {
      showMessage('שגיאת רשת', 'בדוק את החיבור לאינטרנט', 'error');
    }
  };

  // Handle "Forgot Password" click
  const handleForgotPassword = async () => {
    setIsLoading(true);
    await sendOtp();
    setIsLoading(false);
    setStep('forgot-otp');
  };

  // Handle OTP input
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }

    // Auto-submit when complete
    if (index === 5 && value) {
      const otpCode = newOtp.join('');
      if (step === 'forgot-otp') {
        // OTP verified - go to reset password
        handleForgotOtpVerified(otpCode);
      } else if (step === 'device-otp') {
        handleDeviceOtpSubmit(otpCode);
      }
    }
  };

  // Handle OTP key down for backspace
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handle forgot-otp verification → go to reset-password step
  const handleForgotOtpVerified = (otpCode: string) => {
    // Store OTP for reset-password call, move to password reset step
    setOtp(otpCode.split(''));
    setNewPassword('');
    setConfirmPassword('');
    setStep('reset-password');
  };

  // Handle device OTP submit → verify then retry login
  const handleDeviceOtpSubmit = async (otpCode: string) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const deviceFingerprint = await getDeviceFingerprint();

      // Verify OTP to register device (use actual phone, not username/email)
      const otpTarget = otpPhone || phone;
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Phone: otpTarget, Otp: otpCode, DeviceFingerprint: deviceFingerprint }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Device is now registered. Retry login with password.
        const result = await authApi.parentLogin(phone, password, deviceFingerprint);

        if (result.success && result.data) {
          if (result.data.requiresPasswordChange) {
            setTempToken(result.data.accessToken || result.data.token);
            setTempUser(result.data);
            setNewPassword('');
            setConfirmPassword('');
            setStep('force-change');
            return;
          }
          completeLogin(result.data);
        } else {
          showMessage('שגיאה', result.message || 'שגיאה בכניסה', 'error');
          setStep('password');
        }
      } else {
        showMessage('שגיאה', data.message || 'קוד אימות שגוי', 'error');
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
      }
    } catch (error) {
      console.error('Device OTP error:', error);
      showMessage('שגיאת רשת', 'בדוק את החיבור לאינטרנט ונסה שוב', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset-password submit
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate new password
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      showMessage('סיסמה לא תקינה', `הסיסמה חייבת לכלול: ${errors.join(', ')}`, 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('שגיאה', 'הסיסמאות אינן תואמות', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const otpCode = otp.join('');
      const result = await authApi.resetPassword(phone, otpCode, newPassword);

      if (result.success && result.data) {
        showMessage('הצלחה', 'הסיסמה שונתה בהצלחה', 'success');
        completeLogin(result.data);
      } else {
        showMessage('שגיאה', result.message || 'שגיאה באיפוס הסיסמה', 'error');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'שגיאת רשת';
      showMessage('שגיאה', msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle force-change password submit
  const handleForceChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      showMessage('סיסמה לא תקינה', `הסיסמה חייבת לכלול: ${errors.join(', ')}`, 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('שגיאה', 'הסיסמאות אינן תואמות', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Temporarily set the token so the API call includes it
      if (tempToken) {
        setToken(tempToken);
      }

      const result = await authApi.changePassword(password, newPassword);

      if (result.success && result.data) {
        completeLogin(result.data);
      } else {
        showMessage('שגיאה', result.message || 'שגיאה בשינוי הסיסמה', 'error');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'שגיאת רשת';
      showMessage('שגיאה', msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Back button handler
  const handleBack = () => {
    switch (step) {
      case 'password':
        setStep('phone');
        setPassword('');
        break;
      case 'forgot-otp':
        setStep('password');
        setOtp(['', '', '', '', '', '']);
        break;
      case 'reset-password':
        setStep('forgot-otp');
        setNewPassword('');
        setConfirmPassword('');
        break;
      case 'device-otp':
        setStep('password');
        setOtp(['', '', '', '', '', '']);
        break;
      case 'force-change':
        // Cannot go back from force change - must change password
        break;
      default:
        navigate('/');
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendAttempts >= MAX_RESEND_ATTEMPTS) return;
    const otpTarget = otpPhone || phone;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Phone: otpTarget }),
      });
      const data = await response.json();
      if (data.success) {
        setResendAttempts(prev => prev + 1);
        setCooldownSeconds(OTP_EXPIRATION_SECONDS);
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
        showMessage('נשלח בהצלחה', 'קוד אימות חדש נשלח לטלפון שלך', 'success');

        if (data.data?.devOtp) {
          console.log('%c DEV MODE - OTP Code: ' + data.data.devOtp, 'background: #22c55e; color: white; padding: 8px; font-size: 16px;');
          showMessage('מצב פיתוח', `קוד OTP: ${data.data.devOtp}`, 'info');
        }
      } else {
        showMessage('שגיאה', data.message || 'שגיאה בשליחת קוד', 'error');
      }
    } catch {
      showMessage('שגיאת רשת', 'בדוק את החיבור לאינטרנט', 'error');
    }
  };

  // Password requirements display
  const passwordErrors = newPassword ? validatePassword(newPassword) : [];
  const isNewPasswordValid = newPassword.length > 0 && passwordErrors.length === 0;
  const doPasswordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column' as const,
      background: 'linear-gradient(180deg, #fb923c 0%, #f97316 50%, #ea580c 100%)',
      direction: 'rtl' as const,
    },
    header: {
      padding: '16px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    backButton: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: 'white',
    },
    themeToggle: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: 'white',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      padding: '20px',
    },
    logoContainer: {
      marginBottom: '40px',
      textAlign: 'center' as const,
    },
    logoCircle: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      background: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    },
    title: {
      color: 'white',
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    subtitle: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: '16px',
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      padding: '32px 24px',
      width: '100%',
      maxWidth: '360px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      boxSizing: 'border-box' as const,
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#1f2937',
      textAlign: 'center' as const,
      marginBottom: '8px',
    },
    cardSubtitle: {
      fontSize: '14px',
      color: '#6b7280',
      textAlign: 'center' as const,
      marginBottom: '24px',
    },
    inputContainer: {
      position: 'relative' as const,
      marginBottom: '20px',
      width: '100%',
    },
    input: {
      width: '100%',
      padding: '16px',
      paddingRight: '48px',
      fontSize: '18px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      outline: 'none',
      direction: 'ltr' as const,
      textAlign: 'right' as const,
      transition: 'border-color 0.2s',
      boxSizing: 'border-box' as const,
    },
    passwordInput: {
      width: '100%',
      padding: '16px',
      paddingRight: '48px',
      paddingLeft: '48px',
      fontSize: '18px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      outline: 'none',
      direction: 'ltr' as const,
      textAlign: 'right' as const,
      transition: 'border-color 0.2s',
      boxSizing: 'border-box' as const,
    },
    inputIcon: {
      position: 'absolute' as const,
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      pointerEvents: 'none' as const,
    },
    eyeButton: {
      position: 'absolute' as const,
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    button: {
      width: '100%',
      padding: '16px',
      fontSize: '18px',
      fontWeight: 'bold',
      color: 'white',
      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    otpContainer: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'center',
      marginBottom: '24px',
      direction: 'ltr' as const,
    },
    otpInput: {
      width: '42px',
      height: '48px',
      fontSize: '20px',
      fontWeight: 'bold',
      textAlign: 'center' as const,
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    forgotLink: {
      display: 'block',
      textAlign: 'center' as const,
      marginTop: '12px',
      color: '#f97316',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      width: '100%',
    },
    resendText: {
      textAlign: 'center' as const,
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '16px',
    },
    resendLink: {
      color: '#f97316',
      fontWeight: 'bold',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      fontSize: '14px',
    },
    footer: {
      padding: '20px',
      textAlign: 'center' as const,
    },
    footerText: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '12px',
    },
    requirement: {
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  };

  // Render OTP section (reused for forgot-otp and device-otp)
  const renderOtpSection = (title: string, subtitle: string) => (
    <motion.div
      key={step}
      style={styles.card}
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -100, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <CheckCircle size={48} color="#22c55e" style={{ margin: '0 auto' }} />
      </div>
      <h2 style={styles.cardTitle}>{title}</h2>
      <p style={styles.cardSubtitle}>{subtitle}</p>

      <FormLabel text="קוד אימות" helpText={parentLoginHelpTexts.otp.tooltip} style={{ color: '#1f2937', justifyContent: 'center', marginBottom: '8px' }} />
      <div style={styles.otpContainer}>
        {otp.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            autoFocus={index === 0}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            style={{
              ...styles.otpInput,
              borderColor: digit ? '#f97316' : '#e5e7eb',
            }}
          />
        ))}
      </div>
      <HelperText text={parentLoginHelpTexts.otp.helper!} />

      <button
        type="button"
        onClick={() => {
          const otpCode = otp.join('');
          if (step === 'forgot-otp') handleForgotOtpVerified(otpCode);
          else if (step === 'device-otp') handleDeviceOtpSubmit(otpCode);
        }}
        style={{
          ...styles.button,
          ...(isLoading || otp.some(d => !d) ? styles.buttonDisabled : {}),
        }}
        disabled={isLoading || otp.some(d => !d)}
      >
        {isLoading ? 'מאמת...' : 'אימות'}
      </button>

      {/* Cooldown Timer */}
      {cooldownSeconds > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: '#fef3c7',
          borderRadius: '12px',
          marginTop: '16px',
        }}>
          <Clock size={18} color="#d97706" />
          <span style={{ color: '#92400e', fontSize: '14px', fontWeight: '500' }}>
            הקוד תקף עוד {formatTime(cooldownSeconds)}
          </span>
        </div>
      )}

      <p style={styles.resendText}>
        {resendAttempts >= MAX_RESEND_ATTEMPTS ? (
          <span style={{ color: '#dc2626' }}>
            יותר מידי ניסיונות. נסו שוב במועד מאוחר יותר
          </span>
        ) : cooldownSeconds > 0 ? (
          <span>
            לא קיבלתם קוד? ניתן לשלוח שוב בעוד {formatTime(cooldownSeconds)}
          </span>
        ) : (
          <>
            לא קיבלתם קוד?{' '}
            <button style={styles.resendLink} onClick={handleResendOtp}>
              שלח שוב
            </button>
          </>
        )}
      </p>
    </motion.div>
  );

  // Render new password fields (reused for reset-password and force-change)
  const renderNewPasswordFields = (onSubmit: (e: React.FormEvent) => void, submitText: string) => (
    <motion.div
      key={step}
      style={styles.card}
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -100, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <KeyRound size={48} color="#f97316" style={{ margin: '0 auto' }} />
      </div>
      <h2 style={styles.cardTitle}>
        {step === 'force-change' ? 'יש לשנות סיסמה' : 'סיסמה חדשה'}
      </h2>
      <p style={styles.cardSubtitle}>
        {step === 'force-change'
          ? 'תוקף הסיסמה שלך פג. יש להגדיר סיסמה חדשה'
          : 'הגדירו סיסמה חדשה לחשבון שלכם'}
      </p>

      <form onSubmit={onSubmit}>
        {/* New Password */}
        <FormLabel text="סיסמה חדשה" helpText={parentLoginHelpTexts.newPassword.tooltip} style={{ color: '#1f2937' }} />
        <div style={styles.inputContainer}>
          <Lock size={20} style={styles.inputIcon} />
          <input
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="סיסמה חדשה"
            style={styles.passwordInput}
            required
          />
          <button
            type="button"
            style={styles.eyeButton}
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <HelperText text={parentLoginHelpTexts.newPassword.helper!} />

        {/* Password requirements */}
        {newPassword && (
          <div style={{ marginBottom: '16px', marginTop: '-12px' }}>
            {[
              { label: 'לפחות 8 תווים', valid: newPassword.length >= 8 },
              { label: 'אות גדולה באנגלית', valid: /[A-Z]/.test(newPassword) },
              { label: 'אות קטנה באנגלית', valid: /[a-z]/.test(newPassword) },
              { label: 'ספרה', valid: /[0-9]/.test(newPassword) },
              { label: 'תו מיוחד', valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) },
            ].map((req, i) => (
              <div key={i} style={{
                ...styles.requirement,
                color: req.valid ? '#22c55e' : '#ef4444',
              }}>
                {req.valid ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                {req.label}
              </div>
            ))}
          </div>
        )}

        {/* Confirm Password */}
        <FormLabel text="אישור סיסמה" helpText={parentLoginHelpTexts.confirmPassword.tooltip} style={{ color: '#1f2937' }} />
        <div style={styles.inputContainer}>
          <Lock size={20} style={styles.inputIcon} />
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="אישור סיסמה"
            style={styles.passwordInput}
            required
          />
          <button
            type="button"
            style={styles.eyeButton}
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <HelperText text={parentLoginHelpTexts.confirmPassword.helper!} />

        {/* Match indicator */}
        {confirmPassword && (
          <div style={{
            ...styles.requirement,
            marginBottom: '16px',
            marginTop: '-12px',
            color: doPasswordsMatch ? '#22c55e' : '#ef4444',
          }}>
            {doPasswordsMatch ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
            {doPasswordsMatch ? 'הסיסמאות תואמות' : 'הסיסמאות אינן תואמות'}
          </div>
        )}

        <button
          type="submit"
          style={{
            ...styles.button,
            ...(isLoading || !isNewPasswordValid || !doPasswordsMatch ? styles.buttonDisabled : {}),
          }}
          disabled={isLoading || !isNewPasswordValid || !doPasswordsMatch}
        >
          {isLoading ? 'שומר...' : submitText}
        </button>
      </form>
    </motion.div>
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <button
          style={{
            ...styles.backButton,
            ...(step === 'force-change' ? { opacity: 0.3, cursor: 'not-allowed' } : {}),
          }}
          onClick={handleBack}
          disabled={step === 'force-change'}
        >
          <ArrowLeft size={20} />
        </button>
        <button
          style={styles.themeToggle}
          onClick={() => setIsDark(!isDark)}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </header>

      {/* Content */}
      <main style={styles.content}>
        {/* Logo */}
        <motion.div
          style={styles.logoContainer}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div style={styles.logoCircle}>
            <Baby size={48} color="#f97316" />
          </div>
          <h1 style={styles.title}>קיפיקיד</h1>
          <p style={styles.subtitle}>כניסה להורים</p>
        </motion.div>

        {/* Card */}
        <AnimatePresence mode="wait">
          {/* Step: Phone */}
          {step === 'phone' && (
            <motion.div
              key="phone"
              style={styles.card}
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 style={styles.cardTitle}>ברוכים הבאים!</h2>
              <p style={styles.cardSubtitle}>הזינו טלפון, אימייל או שם משתמש</p>

              <form onSubmit={handlePhoneSubmit}>
                <FormLabel text="טלפון / אימייל / שם משתמש" helpText={parentLoginHelpTexts.identifier.tooltip} style={{ color: '#1f2937' }} />
                <div style={styles.inputContainer}>
                  {isPhoneInput ? (
                    <Phone size={20} style={styles.inputIcon} />
                  ) : phone.includes('@') ? (
                    <Mail size={20} style={styles.inputIcon} />
                  ) : (
                    <User size={20} style={styles.inputIcon} />
                  )}
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="טלפון / אימייל / שם משתמש"
                    style={styles.input}
                    required
                  />
                </div>
                <HelperText text={parentLoginHelpTexts.identifier.helper!} />

                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...(!isValidLogin ? styles.buttonDisabled : {}),
                  }}
                  disabled={!isValidLogin}
                >
                  המשך
                </button>
              </form>
            </motion.div>
          )}

          {/* Step: Password */}
          {step === 'password' && (
            <motion.div
              key="password"
              style={styles.card}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 style={styles.cardTitle}>הזינו סיסמה</h2>
              <p style={styles.cardSubtitle}>הזינו את הסיסמה עבור <strong>{phone}</strong></p>

              <form onSubmit={handlePasswordSubmit}>
                <FormLabel text="סיסמה" helpText={parentLoginHelpTexts.password.tooltip} style={{ color: '#1f2937' }} />
                <div style={styles.inputContainer}>
                  <Lock size={20} style={styles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="סיסמה"
                    style={styles.passwordInput}
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...(isLoading || !password ? styles.buttonDisabled : {}),
                  }}
                  disabled={isLoading || !password}
                >
                  {isLoading ? 'מתחבר...' : 'כניסה'}
                </button>
              </form>

              <button
                type="button"
                style={styles.forgotLink}
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                שכחתי סיסמה
              </button>
            </motion.div>
          )}

          {/* Step: Forgot OTP */}
          {step === 'forgot-otp' && renderOtpSection(
            'הזינו את הקוד',
            `שלחנו קוד בן 6 ספרות ל-${phone}`
          )}

          {/* Step: Device OTP */}
          {step === 'device-otp' && renderOtpSection(
            'אימות מכשיר חדש',
            `זיהינו מכשיר חדש. שלחנו קוד אימות ל-${otpPhone || phone}`
          )}

          {/* Step: Reset Password */}
          {step === 'reset-password' && renderNewPasswordFields(handleResetPasswordSubmit, 'שמור סיסמה')}

          {/* Step: Force Change */}
          {step === 'force-change' && renderNewPasswordFields(handleForceChangeSubmit, 'שמור סיסמה חדשה')}
        </AnimatePresence>

        {/* Registration Link - only show on phone/password steps */}
        {(step === 'phone' || step === 'password') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{
              marginTop: '24px',
              textAlign: 'center',
            }}
          >
            <Link
              to="/parent/register"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'white',
                textDecoration: 'none',
                fontSize: '16px',
                fontWeight: '500',
                padding: '12px 24px',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                transition: 'background 0.2s',
              }}
            >
              <UserPlus size={20} />
              משתמש חדש? הירשם כאן
            </Link>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © 2024 קיפיקיד - כל הזכויות שמורות
        </p>
      </footer>

      {/* Message Modal */}
      <MessageModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
    </div>
  );
}
